import { Navigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useMe } from '@/features/auth/hooks';
import { useOnboardingState } from '@/features/dashboard/hooks';
import { routeGuardLocked, termsGateActive } from '@/features/dashboard/onboarding';
import { navItemForPath } from './nav-items';

/**
 * OnboardingRouteGuard — makes the sidebar's first-run locks REAL at the
 * route level. The sidebar renders locked entries as non-links, but a
 * typed URL would previously walk straight past the sequence; this guard
 * bounces locked sections back to the dashboard (where the Getting
 * Started checklist explains what to do).
 *
 * Single source of truth: the SAME navTabLocked rule + useOnboardingState
 * hook the sidebar / drawer / dashboard use, keyed by navItemForPath's
 * prefix match — so /predicate-finder/12 is gated exactly like
 * /predicate-finder, and a future rule change updates the rail and the
 * guard together. Routes outside the nav (profile, team, support, result
 * deep-links) are not gated, matching the rail.
 *
 * While the gate queries load, the hook biases to UNLOCKED — an
 * already-set-up user never gets a redirect flash; a genuinely-locked
 * user sees the page skeleton for a beat, then lands on the dashboard.
 * UX-level only by design: the backend endpoints behind these pages are
 * tenant-scoped and safe regardless (RBAC is not final — see
 * features/dashboard/onboarding.ts).
 */
export function OnboardingRouteGuard({
  path,
  children,
}: {
  path: string;
  children: ReactNode;
}) {
  // Session revoked elsewhere (admin AI-track switch, logout / password change on
  // another device): once /me has resolved to null on an authed route, bounce to
  // /login. getMe skips the api-client's auto-redirect (so login/signup can probe
  // anon state), so without this a polled revocation just sets me=null and does
  // nothing. Paired with useMe's focus + interval re-check, the user is sent out
  // promptly — no manual hard refresh.
  // Rules of hooks: call BOTH hooks unconditionally, BEFORE any early return — an
  // early `return` between them skips useOnboardingState on the session-revoked path
  // and trips React's "rendered fewer hooks than expected" when /me resolves to null.
  const me = useMe();
  const onboarding = useOnboardingState();
  if (me.isFetched && me.data === null) {
    return <Navigate to="/login" replace />;
  }

  if (termsGateActive(onboarding) && path !== '/onboarding/terms') {
    return <Navigate to="/onboarding/terms" replace />;
  }
  const item = navItemForPath(path);
  if (item && routeGuardLocked(item.to, onboarding)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}
